/**
 * Typed API surface. Verb-first naming per the conventions (`fetch*`, `create*`, `update*`).
 *
 * Every function returns the service's shape unchanged. Reshaping here would put a translation
 * layer between the contract and the screen, which is exactly what drifts.
 */

import { api, uploadFile } from './shared_api_client'
import type {
  Activity,
  Bootstrap,
  CandidateList,
  Dashboard,
  DocumentSearchResponse,
  ImportBatch,
  ImportResolution,
  ImportTarget,
  IndexHealth,
  NewOrganization,
  SkillCatalogEntry,
  NewPerson,
  FollowUp,
  Lead,
  Note,
  Opportunity,
  Organization,
  Paged,
  PersonProfile,
  RelationshipRow,
  SavedView,
  SearchResult,
  Tag,
  TalentProfile,
  TfDocument,
} from '../types/tf_types'

export interface RelationshipQuery {
  owner?: string
  strength?: string
  lead_status?: string
  waiting_only?: boolean
  needs_action?: boolean
  talent_only?: boolean
  dormant_days?: number
  q?: string
  sort?: string
  desc?: boolean
  page?: number
  page_size?: number
}

/** Fields the grid can edit in place. Partial by design — one cell at a time. */
export interface RelationshipPatch {
  RelationshipStrength?: string | null
  ContactStatus?: string | null
  LeadStatus?: string | null
  NextMove?: string | null
  NextFollowUpDate?: string | null
  OwnerUserId?: string | null
  Priority?: string | null
  WaitingForResponse?: boolean
  FollowUpIfNoResponseBy?: string | null
  DoNotContact?: boolean
  DoNotContactReason?: string | null
}

export const tfApi = {
  // ---- bootstrap ---------------------------------------------------------
  fetchBootstrap: () => api.get<Bootstrap>('/bootstrap'),

  // ---- relationships -----------------------------------------------------
  fetchRelationships: (q: RelationshipQuery = {}) =>
    api.get<Paged<RelationshipRow>>('/relationships', q as Record<string, unknown>),

  /** The inline-edit call. Only the changed field is sent. */
  updateRelationship: (contactId: number, patch: RelationshipPatch) =>
    api.patch<Record<string, any>>(`/relationships/${contactId}`, patch),

  fetchPersonProfile: (contactId: number) =>
    api.get<PersonProfile>(`/relationships/${contactId}/profile`),

  fetchRelationshipTypes: (contactId: number) =>
    api.get<Array<Record<string, any>>>(`/relationships/${contactId}/types`),

  assignRelationshipType: (contactId: number, relationshipType: string) =>
    api.post(`/relationships/${contactId}/types`, { RelationshipType: relationshipType }),

  /** Next move + follow-up as ONE business event — four writes, one transaction, service-side. */
  setNextMove: (
    contactId: number,
    payload: {
      NextMove?: string | null
      NextFollowUpDate?: string | null
      FollowUpType?: string
      Priority?: string | null
      OwnerUserId?: string | null
      CreateFollowUp?: boolean
    },
  ) => api.post<Record<string, any>>(`/relationships/${contactId}/next-move`, payload),

  setWaiting: (
    contactId: number,
    payload: { WaitingForResponse: boolean; FollowUpIfNoResponseBy?: string | null; Note?: string },
  ) => api.post<Record<string, any>>(`/relationships/${contactId}/waiting`, payload),

  // ---- dashboard and follow-ups -----------------------------------------
  fetchDashboard: (owner?: string, asOf?: string) =>
    api.get<Dashboard>('/dashboard', { owner, as_of: asOf }),

  fetchFollowUps: (params: {
    owner?: string
    follow_up_status?: string
    subject_type?: string
    subject_id?: number
  } = {}) => api.get<Paged<FollowUp>>('/follow-ups', params as Record<string, unknown>),

  /** The daily loop: close, log, link, update last contact, set next move, create next. */
  completeFollowUp: (payload: {
    FollowUpId: number
    CompletionNote?: string
    ActivityType?: string
    Direction?: string
    Outcome?: string
    CreatesWaitingForResponse?: boolean
    FollowUpIfNoResponseBy?: string | null
    NextMove?: string | null
    NextFollowUpDate?: string | null
    NextFollowUpType?: string
  }) => api.post<Record<string, any>>('/follow-ups/complete', payload),

  deferFollowUp: (followUpId: number, followUpDate: string) =>
    api.post<Record<string, any>>('/follow-ups/defer', {
      FollowUpId: followUpId,
      FollowUpDate: followUpDate,
    }),

  createFollowUp: (payload: Record<string, unknown>) =>
    api.post<Record<string, any>>('/follow-ups', payload),

  // ---- activities and notes ---------------------------------------------
  fetchActivities: (params: {
    subject_type?: string
    subject_id?: number
    activity_type?: string
    limit?: number
  } = {}) => api.get<Paged<Activity>>('/activities', params as Record<string, unknown>),

  logActivity: (payload: {
    SubjectType?: string
    SubjectId: number
    ActivityType: string
    Direction?: string
    Subject?: string
    Body?: string
    Outcome?: string
    NextActionText?: string
    CreatesWaitingForResponse?: boolean
  }) => api.post<Record<string, any>>('/activities', { SubjectType: 'PERSON', ...payload }),

  fetchNotes: (subjectType: string, subjectId: number) =>
    api.get<Paged<Note>>('/notes', { subject_type: subjectType, subject_id: subjectId }),

  createNote: (payload: {
    SubjectType?: string
    SubjectId: number
    Title?: string
    BodyHtml?: string
    BodyText?: string
    NoteCategory?: string
    IsPinned?: boolean
  }) => api.post<Note>('/notes', { SubjectType: 'PERSON', ...payload }),

  // ---- creating people and organizations ---------------------------------

  /** One call. Creating the contact, its email and phone rows, the organization and the
   *  relationship profile separately would leave a contact with no relationship profile whenever
   *  one of them failed — a record that is in the database and invisible to every screen. */
  createPerson: (payload: NewPerson) => api.post<{ ContactId: number; DisplayName: string }>('/people', payload),

  // ---- organizations, leads, opportunities -------------------------------
  fetchOrganizations: () => api.get<Paged<Organization>>('/organizations'),

  createOrganization: (payload: NewOrganization) =>
    api.post<Organization>('/organizations', payload),

  /** Edit a person's own details AND their relationship fields in ONE call, so one Save is one
   *  request and a failure cannot leave half the form applied. */
  updatePerson: (contactId: number, payload: Record<string, unknown>) =>
    api.patch<{ ContactId: number; DisplayName: string; Updated: boolean }>(
      `/people/${contactId}`, payload,
    ),

  /** Reassign. An unknown name CREATES the company — people change jobs, and the new employer is
   *  usually not already an account. An empty name detaches them, which is a real state. */
  setPersonOrganization: (contactId: number, organizationName: string | null) =>
    api.post<{
      ContactId: number
      OrganizationId: number | null
      OrganizationName: string | null
      OrganizationCreated: boolean
    }>(`/people/${contactId}/organization`, { OrganizationName: organizationName }),

  /** Soft delete. Activities and notes reference the contact and are the record of what was
   *  actually done, so removing the person keeps them and cancels their open follow-ups. */
  deletePerson: (contactId: number) =>
    api.del<{ ContactId: number; DisplayName: string; CancelledFollowUps: number }>(
      `/people/${contactId}`,
    ),

  restorePerson: (contactId: number) =>
    api.post<{ ContactId: number; Restored: boolean }>(`/people/${contactId}/restore`, {}),

  // ---- import ------------------------------------------------------------

  /** Stages the file for review. NOTHING reaches contacts until commitImport. */
  uploadImport: (file: File, target: ImportTarget) =>
    uploadFile<ImportBatch>('/imports/upload', file, { target }),

  fetchImports: () => api.get<Paged<ImportBatch>>('/imports'),
  fetchImport: (batchId: number) => api.get<ImportBatch>(`/imports/${batchId}`),

  setImportMapping: (batchId: number, mapping: Record<string, string>) =>
    api.patch<ImportBatch>('/imports/mapping', { ImportBatchId: batchId, Mapping: mapping }),

  setImportResolutions: (batchId: number, resolutions: Record<number, ImportResolution>) =>
    api.patch<ImportBatch>('/imports/rows', { ImportBatchId: batchId, Resolutions: resolutions }),

  commitImport: (batchId: number) =>
    api.post<ImportBatch>('/imports/commit', { ImportBatchId: batchId }),

  cancelImport: (batchId: number) =>
    api.post<ImportBatch>('/imports/cancel', { ImportBatchId: batchId }),

  updateOrganization: (companyId: number, patch: Record<string, unknown>) =>
    api.patch<Record<string, any>>(`/organizations/${companyId}`, patch),

  fetchLeads: (params: { lead_status?: string; owner?: string } = {}) =>
    api.get<Paged<Lead>>('/leads', params as Record<string, unknown>),

  createLead: (payload: Record<string, unknown>) => api.post<Lead>('/leads', payload),

  updateLead: (leadId: number, patch: Record<string, unknown>) =>
    api.patch<Lead>(`/leads/${leadId}`, patch),

  qualifyLead: (leadId: number) =>
    api.post<{ LeadId: number; OpportunityId: number }>(`/leads/${leadId}/qualify`, {}),

  fetchOpportunities: (params: { stage?: string; open_only?: boolean } = {}) =>
    api.get<Paged<Opportunity>>('/opportunities', params as Record<string, unknown>),

  createOpportunity: (payload: Record<string, unknown>) =>
    api.post<Opportunity>('/opportunities', payload),

  updateOpportunity: (opportunityId: number, patch: Record<string, unknown>) =>
    api.patch<Opportunity>(`/opportunities/${opportunityId}`, patch),

  // ---- tags, views, search -----------------------------------------------
  fetchTags: () => api.get<Paged<Tag>>('/tags'),
  createTag: (tagName: string) => api.post<Tag>('/tags', { TagName: tagName }),
  assignTag: (tagId: number, subjectId: number, subjectType = 'PERSON') =>
    api.post('/tags/assign', { TagId: tagId, SubjectId: subjectId, SubjectType: subjectType }),
  unassignTag: (tagId: number, subjectId: number, subjectType = 'PERSON') =>
    api.post('/tags/unassign', { TagId: tagId, SubjectId: subjectId, SubjectType: subjectType }),

  fetchSavedViews: (entityType?: string) =>
    api.get<Paged<SavedView>>('/saved-views', { entity_type: entityType }),
  createSavedView: (payload: Record<string, unknown>) =>
    api.post<SavedView>('/saved-views', payload),
  deleteSavedView: (savedViewId: number) => api.del(`/saved-views/${savedViewId}`),

  search: (query: string, entityTypes?: string[]) =>
    api.post<{ Items: SearchResult[]; Total: number; Query: string }>('/search', {
      Query: query,
      EntityTypes: entityTypes ?? ['PERSON', 'ORGANIZATION', 'NOTE', 'ACTIVITY'],
    }),

  // ---- candidates and skills ---------------------------------------------

  /** Everybody except Not Looking and Not a Candidate. Untriaged people are INCLUDED — a recruiter
   *  can rule people out, but cannot rule in somebody they never knew was there. */
  fetchCandidates: (params: {
    q?: string
    skills?: string
    candidate_status?: string
    include_untriaged?: boolean
    with_resume_only?: boolean
  } = {}) => api.get<CandidateList>('/candidates', params as Record<string, unknown>),

  /** Every skill flag that exists, with counts — so a skill filter is picked, never guessed. */
  fetchSkillCatalog: () =>
    api.get<{ Items: SkillCatalogEntry[]; Total: number }>('/skills'),

  setCandidateStatus: (contactId: number, status: string | null) =>
    api.post<{ ContactId: number; CandidateStatus: string | null }>('/candidates/status', {
      ContactId: contactId,
      CandidateStatus: status,
    }),

  upsertTalentDetail: (contactId: number, payload: Record<string, unknown>) =>
    api.post<TalentProfile>('/talent/detail', { ContactId: contactId, ...payload }),

  /** Re-derive skill flags from stored text, with no file. For after the dictionary is extended,
   *  or once llmgateway is back. */
  reparseResume: (documentId: number) =>
    api.post<{ DocumentId: number; Skills: string[]; Qualifications: string[]; UsedModel: boolean }>(
      '/talent/reparse', { DocumentId: documentId },
    ),

  // ---- talent and documents ---------------------------------------------
  fetchTalent: (params: { candidate_status?: string; availability?: string } = {}) =>
    api.get<Paged<TalentProfile>>('/talent', params as Record<string, unknown>),
  upsertTalent: (payload: Record<string, unknown>) =>
    api.post<TalentProfile>('/talent', payload),

  fetchDocuments: (subjectType: string, subjectId: number) =>
    api.get<Paged<TfDocument>>('/documents', {
      subject_type: subjectType,
      subject_id: subjectId,
    }),
  createDocument: (payload: Record<string, unknown>) =>
    api.post<TfDocument>('/documents', payload),

  /** Store, record and index in one call. Always resolves for a stored file — read IndexStatus. */
  uploadDocument: (
    file: File,
    opts: { subjectId: number; subjectType?: string; documentType?: string },
  ) =>
    uploadFile<TfDocument>('/documents/upload', file, {
      subject_id: String(opts.subjectId),
      subject_type: opts.subjectType ?? 'PERSON',
      document_type: opts.documentType ?? 'RESUME',
    }),

  searchDocuments: (query: string, corpus = 'tf-resumes', limit = 20) =>
    api.post<DocumentSearchResponse>('/documents/search', { Query: query, Corpus: corpus, Limit: limit }),

  fetchIndexHealth: () => api.get<IndexHealth>('/documents/index-health'),

  reindexDocument: (documentId: number) =>
    api.post<TfDocument>('/documents/reindex', { DocumentId: documentId }),

  fetchOutreachRisk: (contactIds: number[]) =>
    api.post<{ TotalContacts: number; Blocked: number; Warnings: number; Risks: any[] }>(
      '/outreach-risk',
      { ContactIds: contactIds },
    ),
}
